import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@tonic/shared/components/ui';
import { DashboardChart } from '@tonic/shared/components/ui';
import {
  Download,
  Droplet,
  Users,
  Repeat2,
  Leaf,
  Calendar,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import { mockRefillAnalytics } from './mock-data';

const timeRanges = [
  { value: '7d', label: 'Last 7 Days' },
  { value: '30d', label: 'Last 30 Days' },
  { value: '90d', label: 'Last 90 Days' },
  { value: '12m', label: 'Last 12 Months' },
];

const metrics = [
  {
    title: 'Total Refills',
    value: mockRefillAnalytics.totalRefills.toString(),
    change: '+12.5%',
    trend: 'up',
    icon: Droplet,
  },
  {
    title: 'Active Subscriptions',
    value: mockRefillAnalytics.activeSubscriptions.toString(),
    change: '+8.3%',
    trend: 'up',
    icon: Users,
  },
  {
    title: 'Monthly Refills',
    value: mockRefillAnalytics.averageRefillsPerMonth.toString(),
    change: '+15.2%',
    trend: 'up',
    icon: Repeat2,
  },
  {
    title: 'Environmental Impact',
    value: mockRefillAnalytics.environmentalImpact.plasticSaved,
    change: '+10.5%',
    trend: 'up',
    icon: Leaf,
  },
];

export function RefillDashboardPage() {
  const [timeRange, setTimeRange] = React.useState('30d');

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Refill Analytics</h1>
          <p className="text-sm text-muted-foreground">
            Track refill program performance and metrics
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Calendar className="w-5 h-5 text-muted-foreground" />
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {timeRanges.map((range) => (
                <SelectItem key={range.value} value={range.value}>
                  {range.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metrics.map((metric) => (
          <Card key={metric.title}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {metric.title}
              </CardTitle>
              <metric.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metric.value}</div>
              <div className="flex items-center space-x-2">
                <Badge variant={metric.trend === 'up' ? 'default' : 'destructive'}>
                  {metric.trend === 'up' ? (
                    <ArrowUpRight className="w-4 h-4" />
                  ) : (
                    <ArrowDownRight className="w-4 h-4" />
                  )}
                  {metric.change}
                </Badge>
                <span className="text-sm text-muted-foreground">vs. last period</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Revenue Over Time</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              title="Revenue Trend"
              data={mockRefillAnalytics.revenueData}
              type="line"
              dataKey="value"
              xAxisKey="date"
              height={300}
              colors={['#10B981']}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Container Types</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              title="Container Distribution"
              data={mockRefillAnalytics.containerTypes}
              type="pie"
              dataKey="count"
              xAxisKey="type"
              height={300}
              colors={['#10B981', '#6366F1', '#F59E0B', '#3B82F6']}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Subscription Plans</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              title="Plan Distribution"
              data={mockRefillAnalytics.subscriptionPlans}
              type="pie"
              dataKey="count"
              xAxisKey="plan"
              height={300}
              colors={['#10B981', '#6366F1']}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Refills by Location</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              title="Location Distribution"
              data={mockRefillAnalytics.refillsByLocation}
              type="bar"
              dataKey="count"
              xAxisKey="location"
              height={300}
              colors={['#6366F1']}
            />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Environmental Impact</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-muted-foreground">Plastic Saved</h3>
              <p className="text-2xl font-bold">{mockRefillAnalytics.environmentalImpact.plasticSaved}</p>
            </div>
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-muted-foreground">Water Saved</h3>
              <p className="text-2xl font-bold">{mockRefillAnalytics.environmentalImpact.waterSaved}</p>
            </div>
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-muted-foreground">Carbon Reduced</h3>
              <p className="text-2xl font-bold">{mockRefillAnalytics.environmentalImpact.carbonReduced}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
} 
